/**
 * Collaborative live-session evidence annotator.
 *
 * Both operator and field render this full-screen on top of the LiveKit
 * grid when an `evidence/view.open` envelope is in flight (managed by
 * `useCollabAnnotatorStore`). The component:
 *
 *   - fetches the captured still (image bytes via presigned URL)
 *   - renders the existing bound annotations (showAnnotations defaults
 *     to ON in collab mode — both parties want to see what each other
 *     has already drawn)
 *   - lets EITHER side draw new strokes via touch / pointer; each
 *     stroke is created with `evidence_id` set on the POST so it's born
 *     bound (1 RPC, no follow-up bind), then broadcast over the bus so
 *     the peer's annotator picks it up via useEvidenceAnnotationSync
 *
 * The `useAnnotationStore` (live overlay) is deliberately untouched
 * here — evidence-bound strokes are not "live" and never enter that
 * store. `useAnnotationSync` filters them out on the receiving side.
 *
 * Close: operator hits "Close" → broadcasts `evidence/view.close` →
 * both sides clear `useCollabAnnotatorStore` and the LiveKit grid is
 * visible again. Field-side close is permitted as a safety hatch
 * (operator could disconnect mid-session) but does not broadcast — the
 * operator can re-open the same evidence to resync.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import {
  ANNOTATION_COLORS,
  buildDraft,
  newAnnotationId,
  pointsToPath,
  toolToKind,
  type ToolKind,
} from "@/lib/annotation";
import {
  createAnnotationForEvidence,
  useEvidenceAnnotationSync,
} from "@/lib/annotationSync";
import { useCollabAnnotatorStore } from "@/lib/collabAnnotator";
import { getFieldSessionToken } from "@/lib/fieldSession";
import {
  type LiveDraft as PeerLiveDraft,
  makeDraftBroadcaster,
  useLiveDraftsStore,
} from "@/lib/liveDrafts";
import { useSessionBus } from "@/lib/sessionBus";
import type {
  Annotation,
  AnnotationGeometry,
  ArrowGeometry,
  EllipseGeometry,
  Evidence,
  FreehandGeometry,
  RectGeometry,
} from "@/lib/types";

type Detail = Evidence & {
  download_url: string | null;
  annotations: Annotation[];
};

type Props = {
  evidenceId: string;
  /** Render the close button. Operator side closes for both via the
   *  evidence/view.close envelope; field side closes locally only. */
  side: "operator" | "field";
};

type Draft =
  | { kind: "freehand"; points: [number, number][]; color: string }
  | { kind: "rect"; start: [number, number]; cur: [number, number]; color: string }
  | { kind: "ellipse"; start: [number, number]; cur: [number, number]; color: string }
  | { kind: "arrow"; start: [number, number]; cur: [number, number]; color: string };

const DRAFT_STROKE_WIDTH = 0.006;

export function EvidenceAnnotator({ evidenceId, side }: Props) {
  const { t } = useTranslation();
  const bus = useSessionBus((s) => s.bus);
  const closeStore = useCollabAnnotatorStore((s) => s.close);
  const qc = useQueryClient();
  const [tool, setTool] = useState<ToolKind>("off");
  const [color, setColor] = useState<string>(ANNOTATION_COLORS[0]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Local annotation set — the union of: (a) annotations bound to this
  // evidence at fetch time, (b) strokes either side has added since,
  // delivered via REST round-trips OR via inbound bus envelopes. Keyed
  // by id so dedup works whether a stroke arrives via the broadcast OR
  // via a query refetch.
  const [strokes, setStrokes] = useState<Record<string, Annotation>>({});

  const q = useQuery({
    queryKey: ["evidence-detail", evidenceId],
    queryFn: async () => {
      // Field side has no JWT — attach the X-Field-Session-Token header
      // so the dual-auth EvidenceDetailView accepts the request. The
      // operator side hits the same endpoint via the JWT interceptor
      // on the api instance, so this branch is a no-op for them.
      const fieldToken = getFieldSessionToken();
      const headers = fieldToken
        ? { "X-Field-Session-Token": fieldToken }
        : undefined;
      const res = await api.get<Detail>(
        ApiPaths.evidence(evidenceId),
        headers ? { headers } : undefined,
      );
      return res.data;
    },
  });

  // Initialise / refresh local set when the detail query settles.
  useEffect(() => {
    if (!q.data) return;
    setStrokes((prev) => {
      const next = { ...prev };
      for (const a of q.data!.annotations) next[a.id] = a;
      return next;
    });
  }, [q.data]);

  // Subscribe to peer strokes scoped to this evidence id.
  const onPeerStroke = useCallback((annotation: Annotation) => {
    setStrokes((prev) => ({ ...prev, [annotation.id]: annotation }));
  }, []);
  useEvidenceAnnotationSync(bus, evidenceId, onPeerStroke);

  const create = useMutation({
    mutationFn: async (input: {
      kind: ReturnType<typeof toolToKind>;
      geometry: AnnotationGeometry;
      color: string;
      /** Pre-minted at pointerdown so the live-draft envelopes carry
       *  the same id as the eventual persisted row — peers swap the
       *  transient draft for the persisted stroke without flicker. */
      id: string;
    }) => {
      if (!q.data) throw new Error("evidence detail not loaded");
      const payload = buildDraft(input.kind, input.geometry, input.color);
      // Pass the bus through so the persisted row is broadcast to the
      // peer in the same call. The createAnnotationForEvidence helper
      // hits the server with `evidence_id` set so the row is born bound.
      const annotation = await createAnnotationForEvidence(
        q.data.session,
        evidenceId,
        {
          kind: payload.kind,
          geometry: payload.vector_jsonb,
          color: payload.color,
          stroke_width: payload.stroke_width,
          id: input.id,
        },
        bus,
      );
      return annotation;
    },
    onSuccess: (annotation) => {
      // Local optimistic insert (the broadcast loops back to us via the
      // bus too, but keying by id makes the duplicate a no-op).
      setStrokes((prev) => ({ ...prev, [annotation.id]: annotation }));
      void qc.invalidateQueries({ queryKey: ["session-evidence", q.data?.session] });
      void qc.invalidateQueries({ queryKey: ["evidence-detail", evidenceId] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  function handleClose() {
    closeStore();
    if (side === "operator" && bus) {
      void bus
        .send({
          topic: "evidence",
          type: "view.close",
          payload: { evidence_id: evidenceId },
          requires_ack: false,
        })
        .catch(() => {});
    }
  }

  const annotationList = useMemo(() => Object.values(strokes), [strokes]);

  // Peer's in-progress strokes, scoped to this evidence id. Pulled
  // here (not inside Canvas) so the same hook order runs whether the
  // image is loaded or not.
  const liveDrafts = useLiveDraftsStore((s) => s.byId);
  const peerDrafts = useMemo(
    () => Object.values(liveDrafts).filter((d) => d.evidence_id === evidenceId),
    [liveDrafts, evidenceId],
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.evidenceAnnotator.dialogLabel")}
      className="absolute inset-0 z-40 flex flex-col bg-nb-bg"
    >
      {/* On a phone the metadata + 5 tools + 4 swatches + close button
          won't fit on one row, so the header wraps: the label + close
          stay on the top row and the toolbar drops to its own full-width
          row below (which it can fill + wrap into). The capture metadata
          is hidden on narrow screens — it's reference detail, not needed
          mid-annotation. */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-nb-border bg-nb-bg/95 px-3 py-2 sm:gap-3 sm:px-4">
        <div className="order-1 flex items-center gap-2 sm:gap-3">
          <span className="text-xs uppercase tracking-wide text-nb-mint">
            {t("ui.evidenceAnnotator.collabAnnotate")}
          </span>
          {q.data && (
            <span className="hidden text-xs text-nb-text-2 sm:inline">
              {q.data.kind} · {humanSize(q.data.byte_size)} ·{" "}
              <span className="font-mono">{q.data.sha256.slice(0, 12)}…</span>
            </span>
          )}
        </div>
        <button
          type="button"
          className="btn-ghost order-2 shrink-0 sm:order-3"
          onClick={handleClose}
        >
          {side === "operator"
            ? t("ui.evidenceAnnotator.closeForBoth")
            : t("ui.evidenceAnnotator.close")}
        </button>
        <div className="order-3 w-full sm:order-2 sm:w-auto">
          <CollabToolbar
            tool={tool}
            color={color}
            onTool={setTool}
            onColor={setColor}
            busy={create.isPending}
          />
        </div>
      </header>

      <div className="relative flex-1 overflow-hidden bg-black">
        {q.isLoading && (
          <p className="p-6 text-sm text-nb-text-2">
            {t("ui.evidenceAnnotator.loadingCapture")}
          </p>
        )}
        {q.isError && (
          <p role="alert" className="p-6 text-sm text-cvp-danger">
            {t("ui.evidenceAnnotator.loadError")}
          </p>
        )}
        {q.data && q.data.status === "ready" && q.data.download_url ? (
          <Canvas
            url={q.data.download_url}
            annotations={annotationList}
            peerDrafts={peerDrafts}
            tool={tool}
            color={color}
            draft={draft}
            setDraft={setDraft}
            bus={bus}
            evidenceId={evidenceId}
            onCommit={(input) => create.mutate(input)}
          />
        ) : q.data ? (
          <p className="p-6 text-sm text-nb-text-2">
            {t("ui.evidenceAnnotator.captureStatusPrefix")}{" "}
            <span className="text-cvp-warn">{q.data.status}</span>.{" "}
            {t("ui.evidenceAnnotator.captureStatusSuffix")}
          </p>
        ) : null}
      </div>

      {err && (
        <div
          role="alert"
          className="shrink-0 border-t-2 border-cvp-danger bg-cvp-danger/15 px-4 py-3 text-sm font-medium text-cvp-danger"
        >
          <strong>{t("ui.evidenceAnnotator.saveErrorPrefix")}</strong> {err}
          <button
            type="button"
            onClick={() => setErr(null)}
            className="ml-3 underline opacity-75 hover:opacity-100"
          >
            {t("ui.evidenceAnnotator.dismiss")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------- Canvas + Toolbar (kept local) ------------------- */

function Canvas({
  url,
  annotations,
  peerDrafts,
  tool,
  color,
  draft,
  setDraft,
  bus,
  evidenceId,
  onCommit,
}: {
  url: string;
  annotations: Annotation[];
  peerDrafts: PeerLiveDraft[];
  tool: ToolKind;
  color: string;
  draft: Draft | null;
  setDraft: (d: Draft | null) => void;
  bus: ReturnType<typeof useSessionBus.getState>["bus"];
  evidenceId: string;
  onCommit: (input: {
    kind: ReturnType<typeof toolToKind>;
    geometry: AnnotationGeometry;
    color: string;
    id: string;
  }) => void;
}) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageBox, setImageBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const broadcasterRef = useRef<ReturnType<typeof makeDraftBroadcaster> | null>(
    null,
  );
  const draftIdRef = useRef<string | null>(null);

  useEffect(() => {
    function update() {
      const i = imgRef.current;
      const w = wrapRef.current;
      if (!i || !w) return;
      if (!i.naturalWidth || !i.naturalHeight) return;
      const wRect = w.getBoundingClientRect();
      const iRect = i.getBoundingClientRect();
      setImageBox({
        left: iRect.left - wRect.left,
        top: iRect.top - wRect.top,
        width: iRect.width,
        height: iRect.height,
      });
    }
    update();
    const ro = new ResizeObserver(update);
    if (wrapRef.current) ro.observe(wrapRef.current);
    if (imgRef.current) ro.observe(imgRef.current);
    const img = imgRef.current;
    img?.addEventListener("load", update);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      img?.removeEventListener("load", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const armed = tool !== "off";

  function pointFromEvent(e: React.PointerEvent): [number, number] | null {
    const target = e.currentTarget as SVGSVGElement;
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    return [clamp01(x), clamp01(y)];
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!armed) return;
    const p = pointFromEvent(e);
    if (!p) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    // Mint the future annotation id NOW; the peer's draft envelopes
    // and the eventual `draw.create` envelope share this id so the
    // transient peer draft is replaced by the persisted row cleanly.
    const id = newAnnotationId();
    draftIdRef.current = id;
    broadcasterRef.current = makeDraftBroadcaster(bus, {
      id,
      evidence_id: evidenceId,
      kind: toolToKind(tool),
      color,
      stroke_width: DRAFT_STROKE_WIDTH,
    });
    if (tool === "pen") setDraft({ kind: "freehand", points: [p], color });
    else if (tool === "rect") setDraft({ kind: "rect", start: p, cur: p, color });
    else if (tool === "ellipse") setDraft({ kind: "ellipse", start: p, cur: p, color });
    else if (tool === "arrow") setDraft({ kind: "arrow", start: p, cur: p, color });
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!armed || !draft) return;
    const p = pointFromEvent(e);
    if (!p) return;
    let next: Draft;
    if (draft.kind === "freehand") {
      next = { ...draft, points: [...draft.points, p] };
    } else {
      next = { ...draft, cur: p };
    }
    setDraft(next);
    broadcasterRef.current?.send(draftGeometry(next));
  }

  function onPointerUp() {
    if (!armed || !draft) return;
    const finalised = draft;
    const draftId = draftIdRef.current;
    setDraft(null);
    draftIdRef.current = null;
    broadcasterRef.current = null;
    if (finalised.kind === "freehand" && finalised.points.length < 2) return;
    if (
      (finalised.kind === "rect"
        || finalised.kind === "ellipse"
        || finalised.kind === "arrow") &&
      pointsAlmostEqual(finalised.start, finalised.cur)
    )
      return;
    const geometry = draftGeometry(finalised);
    onCommit({
      kind: toolToKind(tool),
      geometry,
      color: finalised.color,
      id: draftId ?? newAnnotationId(),
    });
  }

  function onPointerCancel() {
    broadcasterRef.current?.end();
    draftIdRef.current = null;
    broadcasterRef.current = null;
    setDraft(null);
  }

  const overlayStyle: React.CSSProperties = imageBox
    ? {
        position: "absolute",
        left: `${imageBox.left}px`,
        top: `${imageBox.top}px`,
        width: `${imageBox.width}px`,
        height: `${imageBox.height}px`,
      }
    : { position: "absolute", inset: 0 };

  return (
    <div ref={wrapRef} className="relative flex h-full w-full items-center justify-center">
      <img
        ref={imgRef}
        src={url}
        alt={t("ui.evidenceAnnotator.imageAlt")}
        className="block max-h-full max-w-full object-contain"
      />
      <svg
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        style={overlayStyle}
        className={
          armed
            ? "pointer-events-auto z-10 cursor-crosshair touch-none"
            : "pointer-events-none z-10"
        }
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {annotations.map((a) => (
          <BoundAnnotation key={a.id} a={a} />
        ))}
        {peerDrafts.map((d) => (
          <PeerDraftAnnotation key={d.id} d={d} />
        ))}
        {draft && <DraftAnnotation draft={draft} />}
      </svg>
    </div>
  );
}

function CollabToolbar({
  tool,
  color,
  onTool,
  onColor,
  busy,
}: {
  tool: ToolKind;
  color: string;
  onTool: (t: ToolKind) => void;
  onColor: (c: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const TOOLS: { id: ToolKind; label: string; symbol: string }[] = [
    { id: "off", label: t("ui.evidenceAnnotator.toolOff"), symbol: "↖" },
    { id: "pen", label: t("ui.evidenceAnnotator.toolPen"), symbol: "✏︎" },
    { id: "rect", label: t("ui.evidenceAnnotator.toolRect"), symbol: "▭" },
    { id: "ellipse", label: t("ui.evidenceAnnotator.toolEllipse"), symbol: "◯" },
    { id: "arrow", label: t("ui.evidenceAnnotator.toolArrow"), symbol: "➜" },
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:justify-start sm:gap-2">
      <div
        className="flex gap-1"
        role="radiogroup"
        aria-label={t("ui.evidenceAnnotator.toolGroupLabel")}
      >
        {TOOLS.map((t) => (
          <button
            key={t.id}
            role="radio"
            aria-checked={tool === t.id}
            aria-label={t.label}
            type="button"
            disabled={busy}
            onClick={() => onTool(t.id)}
            className={`h-9 w-9 rounded-md text-sm transition ${
              tool === t.id
                ? "bg-nb-mint text-black"
                : "text-nb-text hover:bg-nb-surface"
            } ${busy ? "cursor-not-allowed opacity-60" : ""}`}
          >
            {t.symbol}
          </button>
        ))}
      </div>
      <div
        className="flex gap-1"
        role="radiogroup"
        aria-label={t("ui.evidenceAnnotator.colorGroupLabel")}
      >
        {ANNOTATION_COLORS.map((c) => (
          <button
            key={c}
            role="radio"
            aria-checked={color === c}
            aria-label={t("ui.evidenceAnnotator.colorSwatch", { color: c })}
            type="button"
            disabled={busy}
            onClick={() => onColor(c)}
            style={{ backgroundColor: c }}
            className={`h-7 w-7 rounded-full border-2 transition ${
              color === c ? "border-white" : "border-transparent"
            } ${busy ? "cursor-not-allowed opacity-60" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}

function BoundAnnotation({ a }: { a: Annotation }) {
  if (a.kind === "freehand") {
    const g = a.vector_jsonb as FreehandGeometry;
    return (
      <path
        d={pointsToPath(g.points)}
        fill="none"
        stroke={a.color}
        strokeWidth={a.stroke_width}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    );
  }
  if (a.kind === "rect") {
    const g = a.vector_jsonb as RectGeometry;
    return (
      <rect
        x={Math.min(g.x, g.x + g.w)}
        y={Math.min(g.y, g.y + g.h)}
        width={Math.abs(g.w)}
        height={Math.abs(g.h)}
        fill="none"
        stroke={a.color}
        strokeWidth={a.stroke_width}
      />
    );
  }
  if (a.kind === "arrow") {
    const g = a.vector_jsonb as ArrowGeometry;
    const sw = a.stroke_width;
    const dx = g.x2 - g.x1;
    const dy = g.y2 - g.y1;
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = g.x2 - ux * headLen;
    const baseY = g.y2 - uy * headLen;
    return (
      <g>
        <line
          x1={g.x1}
          y1={g.y1}
          x2={baseX}
          y2={baseY}
          stroke={a.color}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${g.x2},${g.y2}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={a.color}
          stroke="none"
        />
      </g>
    );
  }
  const g = a.vector_jsonb as EllipseGeometry;
  return (
    <ellipse
      cx={g.cx}
      cy={g.cy}
      rx={g.rx}
      ry={g.ry}
      fill="none"
      stroke={a.color}
      strokeWidth={a.stroke_width}
    />
  );
}

function PeerDraftAnnotation({ d }: { d: PeerLiveDraft }) {
  if (d.kind === "freehand") {
    const g = d.geometry as FreehandGeometry;
    return (
      <path
        d={pointsToPath(g.points)}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    );
  }
  if (d.kind === "rect") {
    const g = d.geometry as RectGeometry;
    return (
      <rect
        x={Math.min(g.x, g.x + g.w)}
        y={Math.min(g.y, g.y + g.h)}
        width={Math.abs(g.w)}
        height={Math.abs(g.h)}
        fill="none"
        stroke={d.color}
        strokeWidth={d.stroke_width}
      />
    );
  }
  if (d.kind === "arrow") {
    const g = d.geometry as ArrowGeometry;
    const sw = d.stroke_width;
    const dx = g.x2 - g.x1;
    const dy = g.y2 - g.y1;
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = g.x2 - ux * headLen;
    const baseY = g.y2 - uy * headLen;
    return (
      <g opacity={0.85}>
        <line
          x1={g.x1}
          y1={g.y1}
          x2={baseX}
          y2={baseY}
          stroke={d.color}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${g.x2},${g.y2}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={d.color}
          stroke="none"
        />
      </g>
    );
  }
  const g = d.geometry as EllipseGeometry;
  return (
    <ellipse
      cx={g.cx}
      cy={g.cy}
      rx={g.rx}
      ry={g.ry}
      fill="none"
      stroke={d.color}
      strokeWidth={d.stroke_width}
    />
  );
}


function DraftAnnotation({ draft }: { draft: Draft }) {
  if (draft.kind === "freehand") {
    return (
      <path
        d={pointsToPath(draft.points)}
        fill="none"
        stroke={draft.color}
        strokeWidth={DRAFT_STROKE_WIDTH}
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "rect") {
    const x = Math.min(draft.start[0], draft.cur[0]);
    const y = Math.min(draft.start[1], draft.cur[1]);
    const w = Math.abs(draft.cur[0] - draft.start[0]);
    const h = Math.abs(draft.cur[1] - draft.start[1]);
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke={draft.color}
        strokeWidth={DRAFT_STROKE_WIDTH}
        opacity={0.9}
      />
    );
  }
  if (draft.kind === "arrow") {
    const sw = DRAFT_STROKE_WIDTH;
    const dx = draft.cur[0] - draft.start[0];
    const dy = draft.cur[1] - draft.start[1];
    const len = Math.max(Math.hypot(dx, dy), 1e-6);
    const ux = dx / len;
    const uy = dy / len;
    const px = -uy;
    const py = ux;
    const headLen = Math.max(sw * 6, 0.02);
    const headHalfWidth = headLen / 2;
    const baseX = draft.cur[0] - ux * headLen;
    const baseY = draft.cur[1] - uy * headLen;
    return (
      <g opacity={0.9}>
        <line
          x1={draft.start[0]}
          y1={draft.start[1]}
          x2={baseX}
          y2={baseY}
          stroke={draft.color}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <polygon
          points={[
            `${draft.cur[0]},${draft.cur[1]}`,
            `${baseX + px * headHalfWidth},${baseY + py * headHalfWidth}`,
            `${baseX - px * headHalfWidth},${baseY - py * headHalfWidth}`,
          ].join(" ")}
          fill={draft.color}
          stroke="none"
        />
      </g>
    );
  }
  const cx = (draft.start[0] + draft.cur[0]) / 2;
  const cy = (draft.start[1] + draft.cur[1]) / 2;
  const rx = Math.abs(draft.cur[0] - draft.start[0]) / 2;
  const ry = Math.abs(draft.cur[1] - draft.start[1]) / 2;
  return (
    <ellipse
      cx={cx}
      cy={cy}
      rx={rx}
      ry={ry}
      fill="none"
      stroke={draft.color}
      strokeWidth={DRAFT_STROKE_WIDTH}
      opacity={0.9}
    />
  );
}

function draftGeometry(d: Draft): AnnotationGeometry {
  if (d.kind === "freehand") return { points: d.points } satisfies FreehandGeometry;
  if (d.kind === "rect") {
    return {
      x: Math.min(d.start[0], d.cur[0]),
      y: Math.min(d.start[1], d.cur[1]),
      w: Math.abs(d.cur[0] - d.start[0]),
      h: Math.abs(d.cur[1] - d.start[1]),
    } satisfies RectGeometry;
  }
  if (d.kind === "arrow") {
    return {
      x1: d.start[0],
      y1: d.start[1],
      x2: d.cur[0],
      y2: d.cur[1],
    } satisfies ArrowGeometry;
  }
  return {
    cx: (d.start[0] + d.cur[0]) / 2,
    cy: (d.start[1] + d.cur[1]) / 2,
    rx: Math.abs(d.cur[0] - d.start[0]) / 2,
    ry: Math.abs(d.cur[1] - d.start[1]) / 2,
  } satisfies EllipseGeometry;
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function pointsAlmostEqual(a: [number, number], b: [number, number]): boolean {
  return Math.abs(a[0] - b[0]) < 0.002 && Math.abs(a[1] - b[1]) < 0.002;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}